import { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../firebase';
import {
    collection,
    query,
    where,
    onSnapshot,
    doc,
    getDocs,
    Timestamp,
    addDoc,
    updateDoc,
    deleteDoc
} from 'firebase/firestore';

// Product interface based on your data structure
export interface OrderRequestProduct {
    displayName: string;
    itemId: string;
    itemType: string;
    price: number;
    productId: string;
    quantity: number;
    skuId: string;
    tier: string;
    tinyId?: string;
}

// Order Request interface
export interface OrderRequest {
    id: string;
    createdAt: Timestamp | Date | string;
    department: string;
    products: OrderRequestProduct[];
    requestType: string;
    requestedBy: string;
    restaurantId: string;
    status: "Pending" | "Approved" | "Rejected";
    targetDate: Timestamp | Date | string;
    totalAmount: number;
    totalItems: number;
    totalQuantity: number;
    notes?: string;
    approvedBy?: string;
    approvedAt?: Timestamp | Date;
    rejectedBy?: string;
    rejectedAt?: Timestamp | Date;
    rejectionReason?: string;
}

// Filter options
export interface OrderRequestFilters {
    status?: string;
    department?: string;
    requestType?: string;
    restaurantId?: string;
    dateFrom?: Date;
    dateTo?: Date;
}

// Hook props
interface UseOrderRequestsProps {
    restaurantId?: string;
    filters?: OrderRequestFilters;
    realtime?: boolean;
    skip?: boolean;
}

// Return type
export interface UseOrderRequestsReturn {
    requests: OrderRequest[];
    loading: boolean;
    error: Error | null;
    
    // CRUD Operations
    createRequest: (data: Partial<OrderRequest>) => Promise<string>;
    updateRequest: (id: string, data: Partial<OrderRequest>) => Promise<void>;
    deleteRequest: (id: string) => Promise<void>;
    approveRequest: (id: string, approvedBy: string) => Promise<void>;
    rejectRequest: (id: string, rejectedBy: string, reason: string) => Promise<void>;
    
    // Filter management
    setFilters: (filters: OrderRequestFilters) => void;
    clearFilters: () => void;
    
    // Statistics
    statistics: {
        total: number;
        pending: number;
        approved: number;
        rejected: number;
        totalAmount: number;
        byDepartment: Record<string, number>;
        byType: Record<string, number>;
    };
    
    // Pagination
    loadMore: () => Promise<void>;
    hasMore: boolean;
}

export const useOrderRequests = ({
    restaurantId,
    filters: initialFilters = {},
    realtime = true,
    skip = false
}: UseOrderRequestsProps = {}): UseOrderRequestsReturn => {
    
    // State
    const [requests, setRequests] = useState<OrderRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [currentFilters, setCurrentFilters] = useState<OrderRequestFilters>(initialFilters);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [lastDoc, setLastDoc] = useState<any>(null);
    const [hasMore, setHasMore] = useState(true);
    const [statistics, setStatistics] = useState({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        totalAmount: 0,
        byDepartment: {} as Record<string, number>,
        byType: {} as Record<string, number>
    });
    
    // Use ref to track if initial load is done
    const isMounted = useRef(true);
    const unsubscribeRef = useRef<(() => void) | null>(null);
    
    // Helper function to format date for comparison
    const formatDateForFilter = (date: Date): Date => {
        const newDate = new Date(date);
        newDate.setHours(0, 0, 0, 0);
        return newDate;
    };
    
    // Helper function to convert any date to Date object
    const toDateObject = (dateValue: any): Date => {
        if (!dateValue) return new Date();
        if (dateValue instanceof Timestamp) {
            return dateValue.toDate();
        }
        if (dateValue instanceof Date) {
            return dateValue;
        }
        if (typeof dateValue === 'string') {
            return new Date(dateValue);
        }
        if (typeof dateValue === 'number') {
            return new Date(dateValue);
        }
        return new Date();
    };
    
    // Sort requests by createdAt (newest first)
    const sortRequestsByDate = useCallback((requestsList: OrderRequest[]): OrderRequest[] => {
        return [...requestsList].sort((a, b) => {
            const dateA = toDateObject(a.createdAt);
            const dateB = toDateObject(b.createdAt);
            return dateB.getTime() - dateA.getTime();
        });
    }, []);
    
    // Calculate statistics from requests
    const calculateStatistics = useCallback((requestsList: OrderRequest[]) => {
        const stats = {
            total: requestsList.length,
            pending: 0,
            approved: 0,
            rejected: 0,
            totalAmount: 0,
            byDepartment: {} as Record<string, number>,
            byType: {} as Record<string, number>
        };
        
        requestsList.forEach(req => {
            if (req.status === "Pending") stats.pending++;
            if (req.status === "Approved") stats.approved++;
            if (req.status === "Rejected") stats.rejected++;
            stats.totalAmount += req.totalAmount || 0;
            
            if (req.department) {
                stats.byDepartment[req.department] = (stats.byDepartment[req.department] || 0) + 1;
            }
            if (req.requestType) {
                stats.byType[req.requestType] = (stats.byType[req.requestType] || 0) + 1;
            }
        });
        
        setStatistics(stats);
    }, []);
    
    // Apply in-memory date filtering
    const applyDateFilter = useCallback((requestsList: OrderRequest[]): OrderRequest[] => {
        let filtered = [...requestsList];
        
        if (currentFilters.dateFrom) {
            const fromDate = formatDateForFilter(currentFilters.dateFrom);
            filtered = filtered.filter(req => {
                const reqDate = toDateObject(req.createdAt);
                return formatDateForFilter(reqDate) >= fromDate;
            });
        }
        
        if (currentFilters.dateTo) {
            const toDate = formatDateForFilter(currentFilters.dateTo);
            filtered = filtered.filter(req => {
                const reqDate = toDateObject(req.createdAt);
                return formatDateForFilter(reqDate) <= toDate;
            });
        }
        
        return filtered;
    }, [currentFilters.dateFrom, currentFilters.dateTo]);
    
    // Build query constraints (without orderBy to avoid composite index requirement)
    const buildQueryConstraints = useCallback(() => {
        const constraints: any[] = [];
        const collectionRef = collection(db, "OrderRequests");
        
        // Add filters - only add if value exists and not "All"
        if (currentFilters.restaurantId) {
            constraints.push(where("restaurantId", "==", currentFilters.restaurantId));
        }
        if (currentFilters.status && currentFilters.status !== "All Status") {
            constraints.push(where("status", "==", currentFilters.status));
        }
        if (currentFilters.department && currentFilters.department !== "All Departments") {
            constraints.push(where("department", "==", currentFilters.department));
        }
        if (currentFilters.requestType && currentFilters.requestType !== "All Types") {
            constraints.push(where("requestType", "==", currentFilters.requestType));
        }
        
        // Note: We're NOT using orderBy here to avoid requiring composite indexes
        // Sorting will be done on the client side
        
        return { collectionRef, constraints };
    }, [currentFilters]);
    
    // Fetch data
    useEffect(() => {
        if (skip) {
            setLoading(false);
            return;
        }
        
        // Clean up previous subscription
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }
        
        setLoading(true);
        setError(null);
        
        const { collectionRef, constraints } = buildQueryConstraints();
        const q = query(collectionRef, ...constraints);
        
        if (realtime) {
            // Real-time listener
            const unsubscribe = onSnapshot(
                q,
                (snapshot) => {
                    if (!isMounted.current) return;
                    
                    let requestsList: OrderRequest[] = snapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    })) as OrderRequest[];
                    
                    // Apply date filters in memory
                    requestsList = applyDateFilter(requestsList);
                    
                    // Sort on client side (newest first)
                    requestsList = sortRequestsByDate(requestsList);
                    
                    setRequests(requestsList);
                    calculateStatistics(requestsList);
                    setLoading(false);
                    setError(null);
                    
                    // Update pagination state
                    setHasMore(snapshot.docs.length === 10);
                    if (snapshot.docs.length > 0) {
                        setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
                    }
                    
                    console.log(`📊 Fetched ${requestsList.length} requests from Firebase`);
                },
                (err) => {
                    console.error("❌ Error fetching order requests:", err);
                    if (isMounted.current) {
                        setError(err as Error);
                        setLoading(false);
                    }
                }
            );
            
            unsubscribeRef.current = unsubscribe;
            
            return () => {
                if (unsubscribeRef.current) {
                    unsubscribeRef.current();
                    unsubscribeRef.current = null;
                }
            };
        } else {
            // One-time fetch
            const fetchRequests = async () => {
                try {
                    const querySnapshot = await getDocs(q);
                    let requestsList: OrderRequest[] = querySnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    })) as OrderRequest[];
                    
                    requestsList = applyDateFilter(requestsList);
                    requestsList = sortRequestsByDate(requestsList);
                    
                    if (isMounted.current) {
                        setRequests(requestsList);
                        calculateStatistics(requestsList);
                        setLoading(false);
                        setHasMore(querySnapshot.docs.length === 10);
                        if (querySnapshot.docs.length > 0) {
                            setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
                        }
                        console.log(`📊 Fetched ${requestsList.length} requests from Firebase (one-time)`);
                    }
                } catch (err) {
                    console.error("❌ Error fetching order requests:", err);
                    if (isMounted.current) {
                        setError(err as Error);
                        setLoading(false);
                    }
                }
            };
            
            fetchRequests();
        }
        
        return () => {
            isMounted.current = false;
        };
    }, [restaurantId, currentFilters.status, currentFilters.department, currentFilters.requestType, currentFilters.restaurantId, currentFilters.dateFrom, currentFilters.dateTo, realtime, skip, buildQueryConstraints, applyDateFilter, calculateStatistics, sortRequestsByDate]);
    
    // CRUD Operations
    const createRequest = async (data: Partial<OrderRequest>): Promise<string> => {
        try {
            let totalAmount = data.totalAmount || 0;
            let totalQuantity = data.totalQuantity || 0;
            let totalItems = data.totalItems || 0;
            
            if (data.products && data.products.length > 0) {
                totalItems = data.products.length;
                totalQuantity = data.products.reduce((sum, p) => sum + (p.quantity || 0), 0);
                totalAmount = data.products.reduce((sum, p) => sum + ((p.price || 0) * (p.quantity || 0)), 0);
            }
            
            const requestData = {
                ...data,
                totalAmount,
                totalQuantity,
                totalItems,
                createdAt: Timestamp.now(),
                status: data.status || "Pending",
                updatedAt: Timestamp.now()
            };
            
            const docRef = await addDoc(collection(db, "OrderRequests"), requestData);
            console.log("✅ Request created with ID:", docRef.id);
            return docRef.id;
        } catch (err) {
            console.error("❌ Error creating order request:", err);
            throw err;
        }
    };
    
    const updateRequest = async (id: string, data: Partial<OrderRequest>): Promise<void> => {
        try {
            const docRef = doc(db, "OrderRequests", id);
            await updateDoc(docRef, {
                ...data,
                updatedAt: Timestamp.now()
            });
        } catch (err) {
            console.error("❌ Error updating order request:", err);
            throw err;
        }
    };
    
    const deleteRequest = async (id: string): Promise<void> => {
        try {
            const docRef = doc(db, "OrderRequests", id);
            await deleteDoc(docRef);
        } catch (err) {
            console.error("❌ Error deleting order request:", err);
            throw err;
        }
    };
    
    const approveRequest = async (id: string, approvedBy: string): Promise<void> => {
        try {
            const docRef = doc(db, "OrderRequests", id);
            await updateDoc(docRef, {
                status: "Approved",
                approvedBy,
                approvedAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });
            console.log("✅ Request approved:", id);
        } catch (err) {
            console.error("❌ Error approving order request:", err);
            throw err;
        }
    };
    
    const rejectRequest = async (id: string, rejectedBy: string, reason: string): Promise<void> => {
        try {
            const docRef = doc(db, "OrderRequests", id);
            await updateDoc(docRef, {
                status: "Rejected",
                rejectedBy,
                rejectedAt: Timestamp.now(),
                rejectionReason: reason,
                updatedAt: Timestamp.now()
            });
            console.log("✅ Request rejected:", id);
        } catch (err) {
            console.error("❌ Error rejecting order request:", err);
            throw err;
        }
    };
    
    const setFilters = useCallback((newFilters: OrderRequestFilters) => {
        setCurrentFilters(prev => {
            // Only update if filters actually changed
            const hasChanged = JSON.stringify(prev) !== JSON.stringify(newFilters);
            if (hasChanged) {
                console.log("🔍 Updating filters:", newFilters);
                return { ...prev, ...newFilters };
            }
            return prev;
        });
        setLastDoc(null);
        setHasMore(true);
    }, []);
    
    const clearFilters = useCallback(() => {
        setCurrentFilters({});
        setLastDoc(null);
        setHasMore(true);
    }, []);
    
    const loadMore = async (): Promise<void> => {
        // Implement pagination if needed
        console.log("Load more not implemented yet");
    };
    
    return {
        requests,
        loading,
        error,
        createRequest,
        updateRequest,
        deleteRequest,
        approveRequest,
        rejectRequest,
        setFilters,
        clearFilters,
        statistics,
        loadMore,
        hasMore
    };
};